import { api } from './api';
import { TEST_RUN_ENDPOINTS } from '../config/endpoints';
import { TestRun, TestRunCreate, TestRunUpdate, TestRunItem, TestRunItemStatus, TestRunStep, TestRunStepStatus } from '../types/testRun';

export const testRunService = {
    getTestRuns: async (params?: { project_id?: number; status?: string }) => {
        const response = await api.get('/test-runs/', { params });
        return response.data;
    },

    getTestRun: async (id: number) => {
        const response = await api.get(`/test-runs/${id}`);
        return response.data;
    },

    createTestRun: async (testRun: TestRunCreate, testItems?: number[]) => {
        const response = await api.post('/test-runs/', testRun, {
            params: { test_items: testItems }
        });
        return response.data;
    },

    updateTestRun: async (id: number, testRun: TestRunUpdate) => {
        const response = await api.put(`/test-runs/${id}`, testRun);
        return response.data;
    },

    deleteTestRun: async (id: number) => {
        const response = await api.delete(`/test-runs/${id}`);
        return response.data;
    },

    startTestRun: async (id: number) => {
        const response = await api.post(`/test-runs/${id}/start`);
        return response.data;
    },

    completeTestRun: async (id: number) => {
        const response = await api.post(`/test-runs/${id}/complete`);
        return response.data;
    },

    pauseTestRun: async (id: number) => {
        const response = await api.post(`/test-runs/${id}/pause`);
        return response.data;
    },

    cancelTestRun: async (id: number) => {
        const response = await api.post(`/test-runs/${id}/cancel`);
        return response.data;
    },

    async addTestItems(id: number, items: { test_item_id: number; assigned_to_id?: number }[]): Promise<TestRun> {
        try {
            const response = await api.post(TEST_RUN_ENDPOINTS.ADD_ITEMS.replace(':id', id.toString()), items);
            return response.data;
        } catch (error: any) {
            console.error('Error adding test items:', error);
            throw new Error('Failed to add test items');
        }
    },

    async removeTestItem(id: number, testRunItemId: number): Promise<void> {
        try {
            await api.delete(TEST_RUN_ENDPOINTS.REMOVE_ITEM.replace(':id', id.toString()).replace(':test_run_item_id', testRunItemId.toString()));
        } catch (error: any) {
            console.error('Error removing test item:', error);
            throw new Error('Failed to remove test item');
        }
    },

    async assignUser(id: number, userId: number): Promise<TestRun> {
        try {
            const response = await api.post(TEST_RUN_ENDPOINTS.ASSIGN_USER.replace(':id', id.toString()), {
                user_id: userId
            });
            return response.data;
        } catch (error: any) {
            console.error('Error assigning user:', error);
            throw new Error('Failed to assign user');
        }
    },

    async assignUserToItem(itemId: number, userId?: number): Promise<TestRun> {
        try {
            const response = await api.post(TEST_RUN_ENDPOINTS.ASSIGN_USER_TO_ITEM.replace(':item_id', itemId.toString()), {
                user_id: userId
            });
            return response.data;
        } catch (error: any) {
            console.error('Error assigning user to item:', error);
            throw new Error('Failed to assign user to item');
        }
    },

    async updateTestItemStatus(itemId: number, status: TestRunItemStatus): Promise<TestRunItem> {
        try {
            console.log('Updating test item status with data:', { itemId, status });
            console.log('Status type:', typeof status);
            console.log('Status value:', status);
            console.log('Available statuses:', Object.values(TestRunItemStatus));
            console.log('Is status valid:', Object.values(TestRunItemStatus).includes(status));

            const requestData = {
                status: status
            };
            console.log('Request data:', requestData);

            const response = await api.put(
                TEST_RUN_ENDPOINTS.UPDATE_ITEM_STATUS.replace(':item_id', itemId.toString()), 
                requestData
            );
            return response.data;
        } catch (error: any) {
            console.error('Error updating test item status:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                console.error('Response detail:', error.response.data.detail);
                if (Array.isArray(error.response.data.detail)) {
                    console.error('Validation errors:', error.response.data.detail);
                }
            }
            throw new Error(error.response?.data?.detail?.[0]?.msg || 'Failed to update test item status');
        }
    },

    async updateTestItemComment(itemId: number, comment: string): Promise<TestRunItem> {
        try {
            const response = await api.put(TEST_RUN_ENDPOINTS.UPDATE_ITEM_COMMENT.replace(':item_id', itemId.toString()), {
                comment
            });
            return response.data;
        } catch (error: any) {
            console.error('Error updating test item comment:', error);
            throw new Error('Failed to update test item comment');
        }
    },

    async updateTestStepStatus(stepId: number, status: TestRunStepStatus, actualResult?: string): Promise<TestRunStep> {
        try {
            console.log('Updating test step status with data:', { stepId, status, actualResult });
            console.log('Status type:', typeof status);
            console.log('Status value:', status);
            console.log('Available statuses:', Object.values(TestRunStepStatus));
            console.log('Is status valid:', Object.values(TestRunStepStatus).includes(status));

            const params = {
                status,
                ...(actualResult && { actual_result: actualResult })
            };
            console.log('Request params:', params);

            const response = await api.put(
                TEST_RUN_ENDPOINTS.UPDATE_STEP_STATUS.replace(':step_id', stepId.toString()), 
                null,
                { params }
            );
            return response.data;
        } catch (error: any) {
            console.error('Error updating test step status:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                console.error('Response detail:', error.response.data.detail);
                if (Array.isArray(error.response.data.detail)) {
                    console.error('Validation errors:', error.response.data.detail);
                }
            }
            throw new Error(error.response?.data?.detail?.[0]?.msg || 'Failed to update test step status');
        }
    },

    async updateChecklistItemStatus(stepId: number, itemIndex: number, status: TestRunStepStatus): Promise<TestRunStep> {
        try {
            const response = await api.put(TEST_RUN_ENDPOINTS.UPDATE_CHECKLIST_ITEM_STATUS.replace(':step_id', stepId.toString()).replace(':item_index', itemIndex.toString()), {
                status
            });
            return response.data;
        } catch (error: any) {
            console.error('Error updating checklist item status:', error);
            throw new Error('Failed to update checklist item status');
        }
    },

    async updateChecklistItemResult(stepId: number, itemIndex: number, actualResult: string): Promise<TestRunStep> {
        try {
            const response = await api.put(TEST_RUN_ENDPOINTS.UPDATE_CHECKLIST_ITEM_RESULT.replace(':step_id', stepId.toString()).replace(':item_index', itemIndex.toString()), {
                actual_result: actualResult
            });
            return response.data;
        } catch (error: any) {
            console.error('Error updating checklist item result:', error);
            throw new Error('Failed to update checklist item result');
        }
    },

    async createTestStep(itemId: number, step: { step_number: number; status: TestRunStepStatus }): Promise<TestRunStep> {
        try {
            console.log('Creating test step with data:', step);
            console.log('Status type:', typeof step.status);
            console.log('Status value:', step.status);

            const requestData = {
                step_number: step.step_number,
                status: step.status,
                description: '',
                expected_result: '',
                actual_result: '',
                attachments: []
            };
            console.log('Request data:', requestData);

            const response = await api.post(TEST_RUN_ENDPOINTS.CREATE_STEP.replace(':item_id', itemId.toString()), requestData);
            return response.data;
        } catch (error: any) {
            console.error('Error creating test step:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
                console.error('Response detail:', error.response.data.detail);
                if (Array.isArray(error.response.data.detail)) {
                    console.error('Validation errors:', error.response.data.detail);
                }
            }
            throw new Error(error.response?.data?.detail?.[0]?.msg || 'Failed to create test step');
        }
    }
}; 